import {
  combineLatest,
  distinctUntilChanged,
  first,
  fromEvent,
  map,
  of,
} from 'rxjs';
import Decimal, {DecimalSource} from './break_infinity.js';
import {DynamicCost, evaluateCost} from './interfaces/dynamic-value';
import {ri} from './runner-idle';
import {Spell} from './spell';
import {SpellName} from './types/runner-idle.js';
import {all$, fancy} from './utils';
import * as D from './decimal_utils';
import {
  makeBurnTemplate,
  makeConfusionTemplate,
  makeDiscoFeverTemplate,
  makeNorthboundCurseTemplate,
  makePoisonTemplate,
  makeRegenerationTemplate,
  makeWitherTemplate,
  StatusEffect,
} from './status-effect.js';

export const spellNames = [
  'restoreStamina',
  'staminaToMana',
  'emptyStomach',
  'overcharge',
  'minorLight',
  'lighttime',
  'dark',
  'flame',
  'autocook',
  'concentrateTime',
  'crystallizeLight',
  'makeTime',
  'expandWood',
  'blink',
  'expandFood',
  'heal1',
  'regen1',
  'addStatusEffect',
  'takeDamage',
  'witherStamina',
  'witherHealth',
] as const;

export function initializeSpells() {
  /** ADD SPELLS */
  ri.spells.restoreStamina.setup({
    name: 'restoreStamina',
    displayName: 'Restore Stamina',
    description: 'Restore Stamina',
    tags: ['magic', 'manual'],
    cooldown: new Decimal(1),
    cost$: of(
      new Map([
        // ['mana', {value: () => 1, string: () => '1 Mana'}],
      ]) as DynamicCost
    ),
    effect$: of(
      new Map([['stamina', {value: () => 1, string: () => '1'}]]) as DynamicCost
    ),
    secret: true,
    requiredStats$: of({intelligence: 3, magic: 'anyEver'}),
  });

  ri.spells.staminaToMana.setup({
    name: 'staminaToMana',
    displayName: 'Absorb Mana',
    description: 'Absorb Mana, regaining Stamina',
    tags: ['magic', 'manual'],
    cooldown: new Decimal(1),
    cost$: of(
      new Map([
        ['mana', {value: () => 1, string: () => '1 Mana'}],
      ]) as DynamicCost
    ),
    effect$: combineLatest([
      ri.resources.stamina.level$,
      ri.stats.magic.value$,
    ]).pipe(
      distinctUntilChanged(),
      map(
        ([staminaLevel, magicValue]) =>
          new Map([
            [
              'stamina',
              {
                value: () =>
                  staminaLevel.times(
                    new Decimal(magicValue.log2()).plus(1).max(1)
                  ),
                string: () => `Stamina lvl times log_2(1 + INT) (min 1)`,
              },
            ],
          ]) as DynamicCost
      )
    ),
    secret: true,
    requiredResources$: of({mana: 'unlocked'}),
  });

  ri.spells.emptyStomach.setup({
    name: 'emptyStomach',
    displayName: 'Absorb Nutrients',
    description: 'Reduces Satiation to 0',
    tags: ['magic', 'manual'],
    cooldown: new Decimal(10),
    cost$: of(
      new Map([
        [
          'mana',
          {
            value: () => 5,
            string: () => ``,
          },
        ],
      ])
    ),
    effect$: combineLatest([
      ri.stats.magic.value$,
      ri.resources.nutrition.value$,
    ]).pipe(
      map(
        ([magic, nutrition]) =>
          new Map([
            [
              'nutrition',
              {
                value: () => magic.min(nutrition).negate(),
                string: () => ``,
              },
            ],
          ])
      )
    ),
    secret: true,
    requiredStats$: of({intelligence: 10, magic: 5}),
  });

  ri.spells.overcharge.setup({
    name: 'overcharge',
    displayName: 'Overcharge',
    description: 'Double the Effect of your next Spell.',
    tags: ['magic'],
    cost$: of(new Map([['mana', {value: () => 10, string: () => `10 Mana`}]])),
    performSpecial: (step: number, optionIndex: number) => {
      // setTimeout(() => {
      //   fromEvent(ri, 'performed-spell')
      //     .pipe(first())
      //     .subscribe((e) => {
      //       const event = e as CustomEvent;
      //       const effect = event.detail.effect;
      //       ri.logCostEffect(
      //         {name: event.detail.name, displayName: 'Overcharged!'},
      //         event.detail.name,
      //         new Map(),
      //         evaluateCost(effect, step, optionIndex),
      //         ['spell', event.detail.name]
      //       );
      //       // Perform the value-based effects.
      //       for (const [key, {value}] of effect) {
      //         ri.gain(key, value(step, optionIndex));
      //       }
      //     });
      // });
    },
    secret: true,
    requiredStats$: of({intelligence: 100, magic: 50}),
  });

  ri.spells.minorLight.classList.add('light');
  ri.spells.minorLight.setup({
    name: 'minorLight',
    displayName: 'Minor Light',
    description: 'Creates a small amount of light.',
    tags: ['lightMagic'],
    cost$: of(new Map([['mana', {value: () => 1, string: () => '1 Mana'}]])),
    effect$: of(
      new Map([['light', {value: () => 5, string: () => '5 Light'}]])
    ),
    secret: true,
    requiredResources$: of({mana: 'unlocked'}),
  });

  ri.spells.dark.classList.add('dark');
  ri.spells.dark.setup({
    name: 'dark',
    displayName: 'Darkness',
    description: 'Plunges the area into darkness.',
    tags: ['lightMagic'],
    cost$: of(new Map([['mana', {value: () => 10, string: () => '10 mana'}]])),
    effect$: of(new Map([['light', {value: () => -100, string: () => ''}]])),
    cooldown: new Decimal(10),
    secret: true,
    requiredStats$: of({intelligence: 10, lightMagic: 1}),
    requiredResources$: of({time: 300}),
  });

  ri.spells.flame.classList.add('flame');
  ri.spells.flame.setup({
    name: 'flame',
    displayName: 'Minor Flame',
    description:
      'Create a small burst of flame!<br>Cook your food or your enemies!',
    tags: ['fireMagic'],
    cost$: of(new Map([['mana', {value: () => 1, string: () => '1 Mana'}]])),
    effect$: of(new Map([['fire', {value: () => 1, string: () => '1 Fire'}]])),
    secret: true,
    requiredStats$: of({intelligence: 10, fireMagic: 1}),
  });

  ri.spells.autocook.classList.add('autocook');
  ri.spells.autocook.setup({
    name: 'autocook',
    displayName: 'Flamebroil',
    description: 'Create magical flames under some Meat, cooking it.',
    level: 2,
    tags: ['fireMagic'],
    cooldown: new Decimal(2),
    cost$: of(
      new Map([
        ['mana', {value: () => 1, string: () => '1 Mana'}],
        ['meat', {value: () => 1, string: () => '1 Meat'}],
      ])
    ),
    effect$: of(new Map([['cookedMeat', {value: () => 1}]])),
    secret: true,
    requiredStats$: of({intelligence: 10, fireMagic: 5}),
  });

  ri.spells.lighttime.classList.add('lighttime');
  ri.spells.lighttime.setup({
    name: 'lighttime',
    displayName: 'Light Time',
    description: 'Destroys some light to create extra time.',
    tags: ['lightMagic', 'timeMagic'],
    cost: new Map([['light', {value: () => 0, string: () => '100 Light'}]]),
    effect: new Map([['time', {value: () => 1, string: () => '1 Time'}]]),
    value: new Decimal(1),
    secret: true,
    requiredResources$: of({light: 'anyEver'}),
    unlock$: ri.spells.concentrateTime.level$.pipe(map(D.gte(3))),
    visible$: ri.spells.concentrateTime.level$.pipe(map(D.gte(2))),
    persistentUnlock: true,
  });

  ri.spells.concentrateTime.classList.add('concentrateTime');
  ri.spells.concentrateTime.setup({
    name: 'concentrateTime',
    displayName: 'Crystallize Time',
    description:
      'Change your saved Time into more Time storage.<br>The process gives off Mana depending on your understanding of it.',
    tags: ['timeMagic', 'crystalMagic', 'realtime'],
    cooldown: new Decimal(0),
    options: ['Small', 'Medium', 'Large', 'Max'],
    optionsData: {
      Small: {
        chance: () => 1,
        displayName: 'Small',
        cost$: of(
          new Map([
            ['time', {value: () => new Decimal(60), string: () => '60 Time'}],
          ])
        ),
        effect$: of(
          new Map<string, {value: () => DecimalSource; string: () => string}>([
            [
              'timeCrystals',
              {value: () => 60, string: () => '60 Time Crystals'},
            ],
            [
              'mana',
              {
                value: () =>
                  ri.stats.wisdom.value
                    .times(ri.stats.timeMagic.value.log10())
                    .max(0),
                string: () =>
                  `(${ri.stats.wisdom.valueHTML} * log_10(${ri.stats.timeMagic.valueHTML})) mana`,
              },
            ],
          ])
        ),
      },
      Medium: {
        chance: () => 1,
        displayName: 'Medium',
        cost$: of(
          new Map([
            ['time', {value: () => new Decimal(600), string: () => '600 Time'}],
          ])
        ),
        effect$: of(
          new Map<string, {value: () => DecimalSource; string: () => string}>([
            [
              'timeCrystals',
              {value: () => 600, string: () => '600 Time Crystals'},
            ],
            [
              'mana',
              {
                value: () =>
                  ri.stats.wisdom.value
                    .times(ri.stats.timeMagic.value.log10())
                    .times(10)
                    .max(0),
                string: () =>
                  `10 * (${ri.stats.wisdom.valueHTML} * log_10(${ri.stats.timeMagic.valueHTML})) mana`,
              },
            ],
          ])
        ),
      },
      Large: {
        chance: () => 1,
        displayName: 'Large',
        cost$: of(
          new Map([
            [
              'time',
              {value: () => new Decimal(3600), string: () => '3600 Time'},
            ],
          ])
        ),
        effect$: of(
          new Map<string, {value: () => DecimalSource; string: () => string}>([
            [
              'timeCrystals',
              {value: () => 3600, string: () => '3600 Time Crystals'},
            ],
            [
              'mana',
              {
                value: () =>
                  ri.stats.wisdom.value
                    .times(ri.stats.timeMagic.value.log10())
                    .times(60)
                    .max(0),
                string: () =>
                  `60 * (${ri.stats.wisdom.valueHTML} * log_10(${ri.stats.timeMagic.valueHTML})) mana`,
              },
            ],
          ])
        ),
      },
      Max: {
        chance: () => 1,
        displayName: 'Max',
        cost$: of(
          new Map([
            [
              'time',
              {
                value: () => ri.resources.time.value.max(60),
                string: () => 'All Time turns into Capacity',
              },
            ],
          ])
        ),
        effect$: of(
          new Map<string, {value: () => DecimalSource; string: () => string}>([
            [
              'timeCrystals',
              {
                value: () => ri.resources.time.value,
                string: () => '',
              },
            ],
            [
              'mana',
              {
                value: () =>
                  ri.stats.wisdom.value
                    .times(ri.stats.timeMagic.value.log10())
                    .times(ri.resources.time.value)
                    .max(0),
                string: () =>
                  `${ri.stats.wisdom.valueHTML} * log_10(${ri.stats.timeMagic.valueHTML})) Mana per Time`,
              },
            ],
          ])
        ),
      },
    },
    value: new Decimal(1),
    secret: true,
    requiredResources$: of({time: 1800}),
    visible$: ri.resources.time.maxValue$.pipe(map(D.gte(300))),
  });

  ri.spells.crystallizeLight.classList.add('crystallizeLight');
  ri.spells.crystallizeLight.setup({
    name: 'crystallizeLight',
    displayName: 'Crystallize Light',
    description: 'Light, frozen in Time.',
    tags: ['lightMagic', 'crystalMagic'],
    cooldown: new Decimal(0),
    cost$: of(
      new Map([
        ['light', {value: () => new Decimal(10), string: () => '10 Light'}],
        [
          'timeCrystals',
          {value: () => new Decimal(1), string: () => '1 Time Crystal'},
        ],
        [
          'mana',
          {
            value: () =>
              new Decimal(1000)
                .div(ri.stats.lightMagic.value.plus(ri.stats.wisdom.value))
                .max(1),
            string: () =>
              `1000 / (${ri.stats.lightMagic.valueHTML} + ${ri.stats.wisdom.valueHTML}), min 1`,
          },
        ],
      ])
    ),
    effect$: of(
      new Map<string, {value: () => DecimalSource; string: () => string}>([
        ['lightCrystals', {value: () => 1, string: () => '1 Light Crystal'}],
      ])
    ),
    value: new Decimal(1),
    secret: true,
    requiredStats$: of({lightMagic: 2, crystalMagic: 2}),
    unlock$: of(true),
    // conditionString: 'LGT: 2, CRY: 2',
    // unlock$: combineLatest([
    //   ri.stats.lightMagic.value$,
    //   ri.stats.crystalMagic.value$,
    // ]).pipe(map(([lit, cry]) => lit.gte(2) && cry.gte(2))),
    // visible$: combineLatest([
    //   ri.stats.lightMagic.value$,
    //   ri.stats.crystalMagic.value$,
    // ]).pipe(map(([lit, cry]) => lit.gte(0) && cry.gte(0))),
  });

  ri.spells.makeTime.classList.add('makeTime');
  ri.spells.makeTime.setup({
    name: 'makeTime',
    displayName: 'A Stitch in Time',
    description: '...saves nine.<br>Make some time.',
    level: 2,
    tags: ['timeMagic', 'realtime'],
    cost$: of(new Map([['mana', {value: () => 9, string: () => `9 Mana`}]])),
    // cost: new Map([]),
    effect$: of(new Map([['time', {value: () => 9, string: () => '9 Time'}]])),
    cooldown: new Decimal(9),
    secret: true,
    requiredResources$: of({time: 3600}),
  });

  // ri.spells.replay.setup( {
  //   name: 'replay',
  //   displayName: 'Replay',
  //   description: 'Perform your ',
  //   tags: ['arcaneMagic'],
  //   cost: new Map([['mana', {value: () => 10, string: () => `10 Mana`}]]),
  //   secret: true,
  //   conditionString: 'Fill up on Wood',
  //   condition: () => ri.resources.wood.full && ri.resources.mana.unlocked,
  //   visibleCondition: () =>
  //     ri.resources.meat.value.gt(0) && ri.resources.mana.unlocked,
  // });

  ri.spells.expandWood.classList.add('expandWood');
  ri.spells.expandWood.setup({
    name: 'expandWood',
    displayName: 'Woodspace',
    description:
      "It's like hammerspace, but for Wood.<br>Expands your Wood capacity.",
    tags: ['spaceMagic'],
    cost$: of(new Map([['mana', {value: () => 10, string: () => `10 Mana`}]])),
    effect$: of(
      new Map([
        [
          'wood:capacity',
          {
            value: () => ri.stats.spaceMagic.value.max(1).floor(),
            string: () =>
              `${fancy(
                'space',
                ri.stats.spaceMagic.value.toString()
              )} Space Magic, min 1, rounded down`,
          },
        ],
      ])
    ),
    secret: true,
    conditionString: 'Fill up on Wood',
    unlock$: all$([ri.resources.wood.full$, ri.resources.mana.unlock$]),
    visible$: all$([
      ri.resources.meat.value$.pipe(map(D.gt(0))),
      ri.resources.mana.unlock$,
    ]),
  });

  ri.spells.blink.classList.add('blink');
  ri.spells.blink.setup({
    name: 'blink',
    displayName: 'Blink',
    description: 'Look ma, no feet!',
    tags: ['spaceMagic'],
    level: 2,
    cost$: of(
      new Map([
        [
          'mana',
          {
            value: () => ri.stats.spaceMagic.value.max(1).floor(),
            string: () =>
              `${fancy(
                'spaceMagic',
                ri.stats.spaceMagic.value.toString()
              )} Space Magic, min 1, rounded down`,
          },
        ],
      ])
    ),
    effect$: of(
      new Map([
        [
          'distance',
          {
            value: () => ri.stats.spaceMagic.value.sqr().max(1).floor(),
            string: () =>
              `${fancy(
                'spaceMagic',
                ri.stats.spaceMagic.value.toString()
              )} Space Magic squared, min 1, rounded down`,
          },
        ],
      ])
    ),
    conditionString: `${fancy('spaceMagic', 'SPC')}: 1`,
    unlock$: ri.stats.spaceMagic.value$.pipe(map(D.gte(1))),
    visible$: ri.stats.spaceMagic.value$.pipe(map(D.gte(0.1))),
  });

  // ri.spells.expandMeat.classList.add('expandMeat');
  // ri.spells.expandMeat.setup( {
  //   name: 'expandMeat',
  //   displayName: 'Meatspace',
  //   description:
  //     "It's like hammerspace, but for Meat.<br>Expands your Meat capacity.",
  //   tags: ['spaceMagic'],
  //   cost: new Map([['mana', {value: () => 10, string: () => `10 Mana`}]]),
  //   effect: new Map([
  //     [
  //       'meat:capacity',
  //       {
  //         value: () => ri.stats.spaceMagic.value.max(1).floor(),
  //         string: () =>
  //           `${fancy(
  //             'space',
  //             ri.stats.spaceMagic.value.max(1).floor().toString()
  //           )} Space Magic`,
  //       },
  //     ],
  //   ]),
  //   secret: true,
  //   conditionString: 'Fill up on Meat',
  //   condition: () => ri.resources.meat.full && ri.resources.mana.unlocked,
  //   visibleCondition: () =>
  //     ri.resources.meat.value.gt(0) && ri.resources.mana.unlocked,
  // });

  ri.spells.expandFood.classList.add('expandFood');
  ri.spells.expandFood.setup({
    name: 'expandFood',
    displayName: 'Cold Storage',
    description:
      "It's like meatspace, but for omnivores.<br>Expands your Food capacity.",
    level: 3,
    tags: ['spaceMagic'],
    optionsByTag: ['food'],
    optionsData: Object.fromEntries(
      ['meat', 'berry', 'pemmican', 'cookedMeat', 'perch'].map((food) => [
        `${food}_option`,
        {
          displayName: food,
          // unlock$: of(true),
          cost$: of(
            new Map([['mana', {value: () => 10, string: () => `10 Mana`}]])
          ),
          effect$: of(
            new Map([
              [
                `${food}:capacity`,
                {
                  value: () => ri.stats.spaceMagic.value.max(1).floor(),
                  string: () =>
                    `${fancy(
                      'space',
                      ri.stats.spaceMagic.value.max(1).floor().toString()
                    )} Space Magic`,
                },
              ],
            ])
          ),
        },
      ])
    ),
    secret: true,
    requiredStats$: of({spaceMagic: 'anyEver'}),
    unlock$: of(true),
  });

  // ri.spells.expandAllPhysical.classList.add('expandAllPhysical');
  // ri.spells.expandAllPhysical.setup( {
  //   name: 'expandAllPhysical',
  //   displayName: 'Expand Space',
  //   description: '<br>Increases how much stuff you can keep.',
  //   tags: ['spaceMagic'],
  //   cost$: of(new Map([['mana', {value: () => 10, string: () => `10 Mana`}]])),
  //   effect$: of(new Map([
  //     [
  //       'meat:capacity',
  //       {
  //         value: () => ri.stats.spaceMagic.value.max(1).floor(),
  //         string: () =>
  //           `${fancy(
  //             'space',
  //             ri.stats.spaceMagic.value.max(1).floor().toString()
  //           )} Space Magic`,
  //       },
  //     ],
  //   ])),
  //   secret: true,
  //   conditionString: 'Fill up on AllPhysical',
  //   condition: () => ri.resources.meat.full && ri.resources.mana.unlocked,
  //   visibleCondition: () =>
  //     ri.resources.meat.value.gt(0) && ri.resources.mana.unlocked,
  // });

  // ri.spells.magicMissile.classList.add('magicMissile');
  // ri.spells.magicMissile.setup( {
  //   name: 'magicMissile',
  //   displayName: 'Magic Missile',
  //   description:
  //     'A magical missile that deals damage to the target. It has a chance to deal more damage.',
  //   tags: ['combat'],
  //   cost$: of(new Map([['mana', {value: () => 10, string: () => '10 Mana'}]])),
  //   cooldown: new Decimal(1),
  //   // damage: new Decimal(10),
  //   // damageMultiplier: new Decimal(1),
  //   // damageChance: new Decimal(0.1),
  //   conditionString: 'Have a Wisdom above 1',
  //   condition: () => ri.stats.wisdom.value.gt(1),
  //   visibleCondition: () => ri.stats.wisdom.value.gt(1),
  // });

  // ri.spells.photosynthesis.classList.add('photosynthesis');
  // ri.spells.photosynthesis.setup( {
  //   name: 'photosynthesis',
  //   displayName: 'Photosynthesis',
  //   description: 'Absorb light to gain Nutrition.',
  //   tags: ['lightMagic'],
  //   canAfford: () => ri.resources.light.value.gt(0),
  //   cost$: of(new Map([
  //     [
  //       'light',
  //       {
  //         value: () => {
  //           const light = ri.resources.light;
  //           return Decimal.min(light.value.minus(light.minimum).times(1), 5);
  //         },
  //         string: (_ri) => `Up to 5 Light when Light is above 0.`,
  //       },
  //     ],
  //   ])),
  //   effect$: of(new Map([
  //     [
  //       'nutrition',
  //       {
  //         value: () => {
  //           const light = ri.resources.light;
  //           return Decimal.min(
  //             light.value.minus(light.minimum).times(10),
  //             50
  //           );
  //         },
  //         string: () => `Up to 5 Nutrition when Light is above 0.`,
  //       },
  //     ],
  //   ])),
  //   cooldown: new Decimal(10),
  //   conditionString: 'Have a Wisdom above 1',
  //   condition: () => ri.stats.wisdom.value.gt(1),
  //   visibleCondition: () => ri.stats.wisdom.value.gt(1),
  // });

  ri.spells.heal1.setup({
    name: 'heal1',
    displayName: 'Heal Self',
    description: 'Medice, cura te ipsum!',
    tags: ['health'],
    cost$: of(new Map([['mana', {value: () => 10, string: () => '10 Mana'}]])),
    effect$: of(
      new Map([['health', {value: () => 5, string: () => '5 Health'}]])
    ),
    cooldown: new Decimal(10),
    requiredStats$: of({wisdom: 5}),
  });

  ri.spells.regen1.setup({
    name: 'regen1',
    displayName: 'Regenerate',
    description: 'Adds a Regeneration status effect, or extends its duration.',
    tags: ['health'],
    cost$: of(new Map([['mana', {value: () => 10, string: () => '10 Mana'}]])),
    onPerform() {
      ri.addStatusEffect(StatusEffect.create(makeRegenerationTemplate()));
    },
    cooldown: new Decimal(10),
    requiredStats$: of({wisdom: 5}),
  });

  ri.spells.addStatusEffect.setup({
    name: 'addStatusEffect',
    displayName: 'DEV Give self status effect',
    cooldown: new Decimal(0),
    onPerform(step, optionIndex) {
      switch (this.options?.[optionIndex]) {
        case 'Regenerating':
          ri.addStatusEffect(StatusEffect.create(makeRegenerationTemplate()));
          break;
        case 'Poisoned':
          ri.addStatusEffect(StatusEffect.create(makePoisonTemplate()));
          break;
        case 'On Fire':
          ri.addStatusEffect(StatusEffect.create(makeBurnTemplate()));
          break;
        case 'Confusion':
          ri.addStatusEffect(StatusEffect.create(makeConfusionTemplate()));
          break;
        case 'Disco Fever':
          ri.addStatusEffect(StatusEffect.create(makeDiscoFeverTemplate()));
          break;
        case 'Northbound Curse':
          ri.addStatusEffect(
            StatusEffect.create(makeNorthboundCurseTemplate())
          );
          break;
        case 'Wither Stamina':
          ri.addStatusEffect(StatusEffect.create(makeWitherTemplate()));
          break;
        default:
          break;
      }
    },
    options: [
      'Regenerating',
      'Poisoned',
      'On Fire',
      'Confusion',
      'Disco Fever',
      'Northbound Curse',
      'Wither Stamina',
    ],
    unlock$: ri.isDev$,
    visible$: ri.isDev$,
  });

  ri.spells.takeDamage.setup({
    name: 'takeDamage',
    displayName: 'DEV Take 1 Damage from self',
    cooldown: new Decimal(0),
    onPerform(step, optionIndex) {
      ri.takeDamage(new Decimal(1), ri);
    },
    unlock$: ri.isDev$,
    visible$: ri.isDev$,
  });

  ri.spells.witherStamina.setup({
    name: 'witherStamina',
    displayName: 'Wither Stamina',
    cooldown: new Decimal(600),
    effect$: of(new Map([['witherMagic', {value: () => 0.1}]])),
    onPerform(step, optionIndex) {
      ri.addStatusEffect(StatusEffect.create(makeWitherTemplate('stamina')));
    },
    requiredStats$: of({witherMagic: 'anyEver'}),
  });

  ri.spells.witherHealth.setup({
    name: 'witherHealth',
    displayName: 'Wither Health',
    cooldown: new Decimal(600),
    effect$: of(new Map([['witherMagic', {value: () => 0.1}]])),
    onPerform(step, optionIndex) {
      ri.addStatusEffect(StatusEffect.create(makeWitherTemplate('health')));
    },
    requiredStats$: of({witherMagic: 5}),
  });
}
